/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react/jsx-props-no-spreading */
import { Button, Link as SpectrumLink, Text } from "@adobe/react-spectrum";
import {
  RankedTester,
  rankWith,
  RendererProps,
  uiTypeIs,
} from "@jsonforms/core";
import { withJsonFormsLayoutProps, useJsonForms } from "@jsonforms/react";
import React, { useEffect, useState, Suspense } from "react";

// Lodash
import { get } from "lodash";

// Redux
import { useSelector } from "react-redux";

// Utils
import { evaluateContextRule, extractPath } from "../utils/commonUtils";

import { AllIcons } from "../utils/icons";

import Modal from "../components/Modal/Modal";

export const modalButtonRendererTester: RankedTester = rankWith(
  4,
  uiTypeIs("ModalButton")
);

/**
 * Custom renderer for a button.
 */
function ModalButtonRenderer({ uischema, path, visible }: RendererProps) {
  const style = uischema.options?.style;
  const spectrumProps = uischema.options?.spectrumProps;
  const label = uischema.options?.label;
  const modal = uischema.options?.modal;
  const modalId = uischema.options?.modalId;
  const isLinkModal = uischema.options?.isLinkModal;

  let iconJsx = null;
  const iconName = uischema.options?.icon?.name;
  const iconSpectrumProps = uischema.options?.icon?.spectrumProps;

  const [showModal, setShowModal] = useState(false);
  const [disabledActionButton, setDisabledActionButton] = React.useState(false);
  const { config: jsonFormConfig } = useJsonForms();
  const externalHandlers = jsonFormConfig?.lifecycleHooks?.externalHandlers;
  const onShowModal = externalHandlers?.onShowModal;

  const onDisableModalButton =
    jsonFormConfig?.lifecycleHooks?.onSuccessCallbacks
      ?.disabledActionButtonHandler;

  useEffect(() => {
    if (onDisableModalButton) {
      onDisableModalButton({ setDisabledActionButton });
    }
  }, []);

  const buttonPressHandler = () => {
    if (modal) {
      if (onShowModal) {
        onShowModal({ modalId });
      }
      setShowModal(true);
    }
  };

  if (iconName && Object.keys(AllIcons).includes(iconName)) {
    const Icon = AllIcons[iconName];
    iconJsx = (
      <Suspense fallback={<div>Loading...</div>}>
        <Icon {...iconSpectrumProps} />
      </Suspense>
    );
  }

  // Determine if the component should be visible
  let componentVisibility = visible;

  // If there is a context rule, evaluate it and use that visibility rule instead.
  if (uischema.options?.contextRule) {
    // grab the scope from the contextRule
    const scope = extractPath(uischema.options?.contextRule?.condition?.scope);
    // get the redux store state based on the scope provided
    const reduxState = useSelector((state: any) =>
      get(state?.reduxState, scope)
    );
    componentVisibility = evaluateContextRule(
      uischema.options?.contextRule,
      reduxState
    )?.visible;
  }

  return (
    <>
      {isLinkModal ? (
        <SpectrumLink
          UNSAFE_style={style}
          {...spectrumProps}
          data-testid="link"
          onPress={!disabledActionButton && buttonPressHandler}
          isHidden={!componentVisibility}
        >
          {label}
        </SpectrumLink>
      ) : (
        <Button
          {...spectrumProps}
          UNSAFE_style={{ ...style, cursor: "pointer" }}
          onPress={buttonPressHandler}
          isDisabled={disabledActionButton}
          isHidden={!componentVisibility}
        >
          {iconJsx}
          {label && <Text>{label}</Text>}
        </Button>
      )}

      <Modal
        setShowModal={setShowModal}
        showModal={showModal}
        modalProps={modal}
        path={path}
      />
    </>
  );
}

export default withJsonFormsLayoutProps(ModalButtonRenderer);
